import { useMemo } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Dog, Eye } from "lucide-react";

import type { EchoflicksVideoSession } from "../types";
import {
  censusConfidenceLabel,
  censusUnavailableLabel,
  summarizeDogCensus,
  type DogCensusDog,
} from "./dogCensus";

/**
 * "Dog census" — what one GPT vision call counted, before it is allowed to
 * change anything.
 *
 * The census runs in production at `observe`: a single GPT call looks at the
 * uploaded photos and writes down how many dogs it sees (and what they look
 * like), but reaches no prompt. This card is the whole point of that mode —
 * you can compare its answer against the identity pipeline's, echo by echo,
 * without a single delivered film having depended on it.
 *
 * Renders nothing for echoes with no projected census, so it is invisible
 * wherever the lane is disabled.
 */
export const DogCensusCard = ({
  session,
}: {
  session: EchoflicksVideoSession;
}) => {
  const census = useMemo(() => summarizeDogCensus(session), [session]);
  if (!census) return null;

  return (
    <Card data-testid="dog-census-card">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="flex items-center gap-2">
            <Dog className="size-4" />
            Dog census
          </CardTitle>
          <Badge
            variant="outline"
            className="gap-1 border-amber-300 font-normal text-amber-700"
          >
            <Eye className="size-3" />
            Observe only
          </Badge>
        </div>
        <CardDescription>
          One GPT vision call over the uploaded photos — observe only, never
          changes the echo.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {census.status === "unavailable" ? (
          <p className="text-sm text-muted-foreground">
            Census did not run:{" "}
            {censusUnavailableLabel(census.unavailableReason)}
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-lg font-semibold">
                {dogCountLabel(census.dogCount)}
              </span>
              {census.countConfidence ? (
                <Badge variant="secondary" className="font-normal">
                  {censusConfidenceLabel(census.countConfidence)}
                </Badge>
              ) : null}
            </div>

            {census.dogs.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {census.dogs.map((dog, index) => (
                  <Badge
                    key={`${dog.breed}-${index}`}
                    variant="outline"
                    className="font-normal"
                  >
                    {breedChipLabel(dog)}
                  </Badge>
                ))}
              </div>
            ) : null}

            {coverageLine(census.imageCounts) ? (
              <p className="text-sm text-muted-foreground">
                {coverageLine(census.imageCounts)}
              </p>
            ) : null}

            {census.reason ? (
              <p className="text-sm italic text-muted-foreground">
                {census.reason}
              </p>
            ) : null}

            {census.identitySubjectCount !== null ? (
              <p className="text-sm text-gray-600">
                Identity pipeline: {census.identitySubjectCount} subject
                {census.identitySubjectCount === 1 ? "" : "s"}
                {census.identityAgreement === "match"
                  ? " — matches the census"
                  : census.identityAgreement === "differs"
                    ? " — census differs"
                    : ""}
              </p>
            ) : null}
          </>
        )}

        <p className="text-xs text-muted-foreground">
          Visual resemblance only — not pedigree evidence.
          {census.modelId ? <> Model: {census.modelId}.</> : null}
        </p>
      </CardContent>
    </Card>
  );
};

function dogCountLabel(dogCount: number | null): string {
  if (dogCount === null) return "Dog count unavailable";
  if (dogCount === 0) return "No dogs";
  return `${dogCount} dog${dogCount === 1 ? "" : "s"}`;
}

function breedChipLabel(dog: DogCensusDog): string {
  const parts = [dog.breed];
  if (dog.confidence) parts.push(dog.confidence);
  if (dog.photoCount !== null) {
    parts.push(`${dog.photoCount} photo${dog.photoCount === 1 ? "" : "s"}`);
  }
  return parts.join(" · ");
}

function coverageLine(counts: {
  analyzed: number | null;
  available: number | null;
  failed: number | null;
}): string | null {
  let line: string | null = null;
  if (counts.analyzed !== null && counts.available !== null) {
    line = `Analyzed ${counts.analyzed} of ${counts.available} photos`;
  } else if (counts.analyzed !== null) {
    line = `Analyzed ${counts.analyzed} photo${counts.analyzed === 1 ? "" : "s"}`;
  }
  if (line && counts.failed !== null && counts.failed > 0) {
    line += ` · ${counts.failed} failed`;
  }
  return line;
}
